import React, { useRef, useEffect, useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Chart as ChartJS, BarElement, CategoryScale, LinearScale, Tooltip, Legend } from 'chart.js';

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend, ChartDataLabels);

// Create gradient for a given color
const createGradient = (ctx, chartArea, color) => {
    if (!chartArea) return color;
    const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
    // Parse hex to get a brighter and darker version
    gradient.addColorStop(0, color + 'CC');
    gradient.addColorStop(1, color);
    return gradient;
};

const BarChart = ({ labels, datasets, showValues }) => {
    const chartRef = useRef(null);

    const options = useMemo(() => ({
        plugins: {
            datalabels: {
                display: showValues,
                color: '#555',
                anchor: 'end',
                align: 'top',
                offset: -2,
                font: {
                    weight: 'bold',
                    size: 11,
                    family: "'Inter', 'Roboto', sans-serif"
                },
                formatter: (value) => value > 0 ? value : '',
            },
            legend: {
                display: true,
                position: 'top',
                labels: {
                    usePointStyle: true,
                    pointStyle: 'rectRounded',
                    padding: 18,
                    font: {
                        size: 12,
                        weight: '600',
                        family: "'Inter', 'Roboto', sans-serif"
                    },
                    color: '#555'
                }
            },
            tooltip: {
                backgroundColor: 'rgba(30, 30, 30, 0.88)',
                titleColor: '#fff',
                bodyColor: 'rgba(255,255,255,0.85)',
                titleFont: { weight: '700', size: 13 },
                bodyFont: { size: 12 },
                borderWidth: 0,
                padding: 14,
                displayColors: true,
                cornerRadius: 12,
                caretSize: 6,
                usePointStyle: true,
                boxPadding: 6,
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                grid: {
                    display: true,
                    color: 'rgba(0,0,0,0.04)',
                    drawBorder: false,
                },
                border: {
                    display: false,
                },
                ticks: {
                    font: {
                        size: 11,
                        family: "'Inter', 'Roboto', sans-serif"
                    },
                    color: '#999',
                    padding: 8,
                }
            },
            x: {
                grid: {
                    display: false
                },
                border: {
                    display: false,
                },
                ticks: {
                    font: {
                        size: 11,
                        family: "'Inter', 'Roboto', sans-serif"
                    },
                    color: '#777',
                    maxRotation: 45,
                }
            }
        },
        elements: {
            bar: {
                borderRadius: 6,
                borderSkipped: false,
            }
        },
        responsive: true,
        maintainAspectRatio: false,
        animation: {
            duration: 800,
            easing: 'easeOutQuart',
        },
        layout: {
            padding: {
                top: 20,
                left: 4,
                right: 4,
            }
        }
    }), [showValues]);

    // Apply gradients after chart mounts
    const data = useMemo(() => {
        const chart = chartRef.current;
        if (chart) {
            const ctx = chart.ctx;
            const chartArea = chart.chartArea;
            return {
                labels: labels,
                datasets: datasets.map(ds => ({
                    ...ds,
                    backgroundColor: Array.isArray(ds.backgroundColor)
                        ? ds.backgroundColor.map(c => createGradient(ctx, chartArea, c))
                        : createGradient(ctx, chartArea, ds.backgroundColor),
                    borderWidth: 0,
                    hoverBackgroundColor: ds.backgroundColor,
                })),
            };
        }
        return {
            labels: labels,
            datasets: datasets.map(ds => ({
                ...ds,
                borderWidth: 0,
            })),
        };
    }, [labels, datasets]);


    return <Bar ref={chartRef} data={data} options={options} />;
};

export default BarChart;
